import { supabase }        from '../supabase.js';
import { pageQuery }       from '../lib/egress.js';
import { openStudentDrill } from './drills.js';
import { payslipPdfs }     from '../lib/payslip-pdf.js';
import { buildLearnerDocs, downloadPdf, slug } from '../lib/learner-docs.js';
import { renderDocSettings } from './doc-settings.js';
import { PDFDocument }     from 'pdf-lib';

/* The Documents dropdown (docs/learner-payslips.md): what ⬇ Documents downloads and what
   WhatsApp sends, for one learner or the selection. All are ticked by default. */
const DOCS = [
  ['payslips', 'Payslips', 'all months; WhatsApp sends finalised ones only'],
  ['memo',     'Process memo', "the cohort's UIF memo"],
  ['letter',   'Confirmation letter', ''],
  ['salary',   'Salary schedule', ''],
  ['ui19',     'UI-19', ''],
];
const UIF_KINDS = ['memo', 'letter', 'salary', 'ui19'];
const MAX_DOWNLOAD = 100;

const esc = s => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

/* ── Module-level caches + persistent filter state ───────────────────────── */
let _munis       = null;
let _classes     = null;
let _orgSettings = null;
let _viewState   = null;
let _docsCloserWired = false;

async function getMunis() {
  if (_munis) return _munis;
  const { data } = await supabase
    .from('municipalities')
    .select('municipality_id,municipality_name')
    .order('municipality_name');
  _munis = data || [];
  return _munis;
}

async function getClasses() {
  if (_classes) return _classes;
  const { data } = await supabase
    .from('classes')
    .select('class_id, class_name, training_venues(town_id, towns(municipality_id))')
    .eq('is_test', false)
    .order('class_name');
  _classes = (data || []).map(c => ({
    class_id:        c.class_id,
    class_name:      c.class_name,
    municipality_id: c.training_venues?.towns?.municipality_id ?? null,
  }));
  return _classes;
}

async function getOrgSettings() {
  if (_orgSettings) return _orgSettings;
  const { data } = await pageQuery('org_settings');
  _orgSettings = data?.[0] || {};
  return _orgSettings;
}

/* ── Main render ──────────────────────────────────────────────────────────── */
export async function renderPayslips() {
  const el = document.getElementById('view-learner-payslips');
  // Learner pay holders only (the database refuses everyone else too: get_stipend_summary).
  if (window.__access?.learnerPay !== true) {
    el.innerHTML = `<div class="page-header"><h1>Payslips</h1><p>Learner pay isn't switched on for you. Ask an owner or admin (People &amp; rights).</p></div>`;
    return;
  }

  if (!_viewState) {
    // Open on the latest month that has payslips, not today's month (the programme has ended).
    const today = new Date();
    const { data: latest } = await supabase.from('learner_payslips')
      .select('period_year,period_month').order('period_year', { ascending: false })
      .order('period_month', { ascending: false }).limit(1);
    _viewState = {
      year:          latest?.[0]?.period_year  ?? today.getFullYear(),
      month:         latest?.[0]?.period_month ?? today.getMonth() + 1,
      municipality:  null,
      classId:       null,
      learnerStatus:  'active',
      payslipStatus:  '',
      stipendAmount:  '',
      phone:          '',
      docs:           new Set(DOCS.map(([k]) => k)),
      tab:            'payslips',
      selected:       new Set(),
    };
  }
  const state = _viewState;
  if (window.__payslipsTab) { state.tab = window.__payslipsTab; window.__payslipsTab = null; }

  const tabsHtml = `
<div role="tablist" style="display:flex;gap:4px;border-bottom:1px solid var(--border);margin:0 0 16px">
  ${[['payslips', 'Payslips'], ['settings', 'Document settings']].map(([k, l]) => `
  <button role="tab" class="ps-tab" data-tab="${k}" aria-selected="${state.tab === k}"
    style="background:none;border:0;border-bottom:2px solid ${state.tab === k ? 'var(--orange)' : 'transparent'};padding:8px 14px;
           font:inherit;font-size:.9rem;font-weight:600;color:${state.tab === k ? 'var(--text)' : 'var(--text-muted)'};cursor:pointer">${l}</button>`).join('')}
</div>`;
  const wireTabs = () => el.querySelectorAll('.ps-tab').forEach(b => b.addEventListener('click', () => {
    if (state.tab === b.dataset.tab) return;
    state.tab = b.dataset.tab;
    renderPayslips();
  }));

  if (state.tab === 'settings') {
    el.innerHTML = `
<div class="page-header"><h1>Payslips &amp; documents</h1>
  <p>What the confirmation letter, salary schedule and UI-19 print for each cohort, and which classes belong to it.</p></div>
${tabsHtml}<div id="ps-settings"></div>`;
    wireTabs();
    await renderDocSettings(el.querySelector('#ps-settings'));
    return;
  }
  // Learner pay across every class: an admin by role, or the learner-pay switch (docs/admin-area.md).
  const isAdmin = window.__access?.learnerPay === true;
  // Re-open and undo "paid": owner/admin only, as in the database (learner_payslips_set_status).
  const isRoleAdmin = window.__access?.admin === true;
  let waStatus = new Map();   // student_uuid → { has_mobile, status, status_at } (admin only)
  let stored   = new Map();   // student_uuid → { id, status, pdf_path } for this month (learner pay)

  const [munis, classes] = await Promise.all([getMunis(), getClasses()]);
  const muniOpts = munis.map(m =>
    `<option value="${esc(m.municipality_id)}">${esc(m.municipality_name)}</option>`
  ).join('');

  const monthVal = `${state.year}-${String(state.month).padStart(2, '0')}`;

  el.innerHTML = `
<style>
  #ps-bulk-bar { display:none; align-items:center; gap:8px; padding:8px 14px;
    background:var(--surface); border:1px solid var(--border); border-radius:8px; margin-bottom:12px; flex-wrap:wrap; }
  #ps-bulk-bar.visible { display:flex; }
  .ps-slip { display:inline-block; padding:2px 9px; border-radius:12px; font-size:.72rem; font-weight:600; }
  .ps-slip.draft { background:#f1f5f9; color:#475569; }
  .ps-slip.final { background:#dbeafe; color:#1d4ed8; }
  .ps-slip.paid  { background:#d1fae5; color:#065f46; }
  .ps-flag { display:block; font-size:.68rem; color:#b45309; margin-top:3px; white-space:nowrap; }
  .ps-muted { color:var(--text-muted); font-size:.78rem; }
  #ps-month-bar { display:flex; align-items:center; gap:8px; flex-wrap:wrap; padding:10px 14px;
    background:var(--surface); border:1px solid var(--border); border-radius:8px; margin-bottom:12px; font-size:.8rem; }
  .ps-act { padding:5px 14px; font-size:.8rem; border:1px solid var(--border); border-radius:6px;
    background:var(--surface); color:var(--text); cursor:pointer; white-space:nowrap; }
  .ps-act.primary { border-color:#1d4ed8; background:#eff6ff; color:#1d4ed8; }
  .ps-act.paid    { border-color:#065f46; background:#d1fae5; color:#065f46; }
  .ps-act.warn    { border-color:#92400e; background:#fef3c7; color:#92400e; }
  .ps-act:disabled { opacity:.5; cursor:default; }
  .ps-pdf-btn { padding:3px 10px; font-size:.75rem; border:1px solid var(--border);
    border-radius:6px; background:var(--surface); color:var(--text); cursor:pointer; white-space:nowrap; }
  .ps-pdf-btn:hover:not(:disabled) { background:var(--surface-hover,#f0f0f0); }
  .ps-pdf-btn:disabled { opacity:.5; cursor:default; }
  .ps-wa-btn { padding:3px 10px; font-size:.75rem; border:1px solid #15803d; border-radius:6px;
    background:#f0fdf4; color:#15803d; cursor:pointer; white-space:nowrap; margin-left:4px; }
  .ps-wa-btn:hover:not(:disabled) { background:#dcfce7; }
  .ps-wa-btn:disabled { opacity:.4; cursor:default; border-color:var(--border); color:var(--text-muted); background:var(--surface); }
  .ps-wa-status { display:block; font-size:.68rem; margin-top:3px; color:var(--text-muted); white-space:nowrap; }
  .ps-wa-status.sent   { color:#15803d; }
  .ps-wa-status.failed { color:#b91c1c; }
  #ps-tbody td { vertical-align:middle; }
  .ps-wa-status.shared { color:#b45309; cursor:help; }
  #ps-docs { position:relative; }
  #ps-docs > summary { list-style:none; display:flex; align-items:center; justify-content:space-between; gap:8px; padding:5px 8px;
    border:1px solid var(--border); border-radius:6px; background:var(--surface); color:var(--text); font-size:.78rem; font-weight:400;
    min-width:150px; cursor:pointer; }
  #ps-docs > summary::-webkit-details-marker { display:none; }
  #ps-docs > summary::after { content:'▾'; color:var(--text-muted); }
  #ps-docs[open] > summary { border-color:var(--orange); }
  .ps-docs-panel { position:absolute; z-index:20; top:calc(100% + 4px); left:0; width:290px; padding:6px; background:var(--surface);
    border:1px solid var(--border); border-radius:8px; box-shadow:0 10px 24px rgba(0,0,0,.14); }
  .ps-docs-panel label { display:flex; gap:8px; align-items:baseline; padding:7px 8px; font-size:.82rem; font-weight:400; color:var(--text); cursor:pointer; }
  .ps-docs-panel small { color:var(--text-muted); font-size:.72rem; }
  .ps-docs-panel p { margin:4px 8px 4px; padding-top:6px; border-top:1px solid var(--border); font-size:.72rem; color:var(--text-muted); font-weight:400; white-space:normal; }
  #ps-msg { font-size:.8rem; margin:-4px 0 12px; }
</style>

<div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px">
  <div>
    <h1>Payslips &amp; documents</h1>
    <p>Monthly stipend register. A learner who completes the programme gets R&nbsp;2,000.00 less 1% UIF (R&nbsp;1,980.00) for every month they attended; drop-outs are paid per day. Payslips are stored: draft until checked, then final (frozen, PDF stored) and paid.</p>
  </div>
  <div style="display:flex;gap:8px;flex-wrap:wrap">
    <button id="ps-csv-btn" style="display:inline-flex;align-items:center;gap:6px;padding:8px 16px;
      font-size:13px;font-weight:500;border:1px solid var(--border);border-radius:8px;
      background:var(--surface);color:var(--text);cursor:pointer;white-space:nowrap">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
      Export CSV
    </button>
    <button id="ps-stdbank-btn" style="display:inline-flex;align-items:center;gap:6px;padding:8px 16px;
      font-size:13px;font-weight:500;border:1px solid #1d4ed8;border-radius:8px;
      background:#eff6ff;color:#1d4ed8;cursor:pointer;white-space:nowrap">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
      Standard Bank Bulk
    </button>
  </div>
</div>

${tabsHtml}

<div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:16px;padding-bottom:2px">
  <label style="display:flex;align-items:center;gap:4px;font-size:.78rem;font-weight:600;color:var(--text-muted);white-space:nowrap;flex-shrink:0">
    Month
    <input type="month" id="ps-month" value="${monthVal}"
      style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;
             background:var(--surface);color:var(--text);font-size:.78rem;width:126px">
  </label>
  <label style="display:flex;align-items:center;gap:4px;font-size:.78rem;font-weight:600;color:var(--text-muted);white-space:nowrap;flex-shrink:0">
    Municipality
    <select id="ps-muni" style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;
      background:var(--surface);color:var(--text);font-size:.78rem;width:136px">
      <option value="">All municipalities</option>
      ${muniOpts}
    </select>
  </label>
  <label style="display:flex;align-items:center;gap:4px;font-size:.78rem;font-weight:600;color:var(--text-muted);white-space:nowrap;flex-shrink:0">
    Class
    <select id="ps-class" style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;
      background:var(--surface);color:var(--text);font-size:.78rem;width:152px">
      <option value="">All classes</option>
    </select>
  </label>
  <label style="display:flex;align-items:center;gap:4px;font-size:.78rem;font-weight:600;color:var(--text-muted);white-space:nowrap;flex-shrink:0">
    Learner Status
    <select id="ps-learner-status" style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;
      background:var(--surface);color:var(--text);font-size:.78rem;width:96px">
      <option value="active">Active</option>
      <option value="">All</option>
      <option value="withdrawn">Dropout</option>
      <option value="graduated">Graduated</option>
      <option value="suspended">Suspended</option>
      <option value="transferred">Transferred</option>
      <option value="inactive">Inactive</option>
      <option value="test">Test</option>
    </select>
  </label>
  <label style="display:flex;align-items:center;gap:4px;font-size:.78rem;font-weight:600;color:var(--text-muted);white-space:nowrap;flex-shrink:0">
    Payslip
    <select id="ps-payslip-status" style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;
      background:var(--surface);color:var(--text);font-size:.78rem;width:138px">
      <option value="">All</option>
      <option value="draft">Draft</option>
      <option value="final">Final</option>
      <option value="paid">Paid</option>
      <option value="changed">Register changed</option>
      <option value="none">No payslip (R0)</option>
    </select>
  </label>
  <label style="display:flex;align-items:center;gap:4px;font-size:.78rem;font-weight:600;color:var(--text-muted);white-space:nowrap;flex-shrink:0">
    Stipend
    <select id="ps-stipend" style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;
      background:var(--surface);color:var(--text);font-size:.78rem;width:120px">
      <option value="">All amounts</option>
      <option value="0">R0</option>
      <option value="1">R1 – R499</option>
      <option value="500">R500 – R999</option>
      <option value="1000">R1 000 – R1 499</option>
      <option value="1500">R1 500 – R2 000</option>
    </select>
  </label>
  <label style="display:flex;align-items:center;gap:4px;font-size:.78rem;font-weight:600;color:var(--text-muted);white-space:nowrap;flex-shrink:0">
    Cell number
    <select id="ps-phone" title="Learners who share a cell number with someone else" style="padding:5px 8px;border:1px solid var(--border);border-radius:6px;
      background:var(--surface);color:var(--text);font-size:.78rem;width:136px">
      <option value="">All</option>
      <option value="own">Own number</option>
      <option value="shared">Shared number</option>
      <option value="none">No number</option>
    </select>
  </label>
  <div style="display:flex;align-items:center;gap:4px;font-size:.78rem;font-weight:600;color:var(--text-muted);white-space:nowrap;flex-shrink:0">
    Documents
    <details id="ps-docs">
      <summary id="ps-docs-label" aria-label="Documents to download or send"></summary>
      <div class="ps-docs-panel">
        ${DOCS.map(([k, label, note]) => `<label><input type="checkbox" class="ps-doc" value="${k}" ${state.docs.has(k) ? 'checked' : ''}>
          <span>${label}${note ? ` <small>(${note})</small>` : ''}</span></label>`).join('')}
        <p>Used by ⬇ Documents and Send on WhatsApp, for one learner or everyone selected.</p>
      </div>
    </details>
  </div>
</div>

<div id="ps-totals" style="display:flex;gap:24px;align-items:center;padding:14px 20px;
  background:var(--surface);border:1px solid var(--border);border-radius:10px;margin-bottom:16px;flex-wrap:wrap">
  <span style="color:var(--text-muted);font-size:.83rem">Loading…</span>
</div>

${isAdmin ? `<div id="ps-month-bar">
  <span id="ps-month-summary" class="ps-muted"></span>
  <span style="flex:1"></span>
  <button class="ps-act" id="ps-recalc" title="Refresh this month's DRAFT payslips from the register. Final and paid payslips are never changed.">Recalculate drafts</button>
  <button class="ps-act primary" id="ps-finalise-shown" title="Finalise every draft in the list as filtered, and store its PDF.">Finalise drafts shown</button>
  <span id="ps-month-msg" style="flex-basis:100%;font-size:.78rem"></span>
</div>` : ''}

<div id="ps-bulk-bar">
  <span id="ps-sel-count" style="font-size:.82rem;color:var(--text-muted);margin-right:4px"></span>
  ${isAdmin ? `<button class="ps-act" id="ps-dl-bulk">⬇ Download documents</button>` : ''}
  ${isAdmin ? `
  <button class="ps-act primary ps-bulk-btn" data-action="finalise">Finalise</button>
  <button class="ps-act paid ps-bulk-btn" data-action="mark_paid">Mark paid</button>
  ${isRoleAdmin ? `
  <button class="ps-act warn ps-bulk-btn" data-action="unmark_paid" title="Paid → final">Undo paid</button>
  <button class="ps-act warn ps-bulk-btn" data-action="reopen" title="Final → draft: recalculated from the register, PDF discarded">Re-open</button>` : ''}` : ''}
  ${isAdmin ? `<button id="ps-wa-bulk"
    style="padding:5px 14px;font-size:.8rem;border:1px solid #15803d;border-radius:6px;background:#f0fdf4;color:#15803d;cursor:pointer;margin-left:auto">
    Send on WhatsApp
  </button>` : ''}
</div>
<p id="ps-msg"></p>

<div class="table-card">
  <table class="data-table">
    <thead><tr>
      <th style="width:32px"><input type="checkbox" id="ps-select-all" title="Select all"></th>
      <th>ID Number</th>
      <th>Name</th>
      <th style="text-align:right">Days</th>
      <th>Bank</th>
      <th>Account</th>
      <th style="text-align:right">Stipend</th>
      <th>Payslip</th>
      <th></th>
    </tr></thead>
    <tbody id="ps-tbody"><tr><td colspan="9" class="no-data">Loading…</td></tr></tbody>
  </table>
</div>`;

  /* Restore filter selections when view re-renders (e.g. navigation back).
     ps-class is restored below, after populateClassSelect() rebuilds its options. */
  document.getElementById('ps-muni').value           = state.municipality || '';
  document.getElementById('ps-learner-status').value = state.learnerStatus;
  document.getElementById('ps-payslip-status').value = state.payslipStatus;
  document.getElementById('ps-stipend').value        = state.stipendAmount;
  document.getElementById('ps-phone').value          = state.phone;
  wireTabs();

  let rows = [];

  const docsLabel = () => {
    const n = state.docs.size;
    return n === DOCS.length ? `All ${DOCS.length} documents` : n === 0 ? 'None chosen'
      : n === 1 ? DOCS.find(([k]) => state.docs.has(k))[1] : `${n} documents`;
  };
  const showDocsLabel = () => {
    document.getElementById('ps-docs-label').textContent = docsLabel();
    updateBulkBar();
  };
  const msg = (text, colour = 'var(--text-muted)') => {
    const m = document.getElementById('ps-msg');
    m.style.color = colour; m.textContent = text;
  };

  /* ── Reload ──────────────────────────────────────────────────────────────── */
  async function reload() {
    state.selected.clear();
    updateBulkBar();
    document.getElementById('ps-select-all').checked = false;
    document.getElementById('ps-tbody').innerHTML =
      `<tr><td colspan="9" class="no-data">Loading…</td></tr>`;

    const params = {
      p_year:            state.year,
      p_month:           state.month,
      p_municipality_id: state.municipality    || null,
      p_class_id:        state.classId         || null,
      p_learner_status:  state.learnerStatus   || null,
    };

    let allData = [], from = 0;
    const PAGE = 1000;
    let error = null;
    while (true) {
      const { data: page, error: err } = await supabase
        .rpc('get_stipend_summary', params)
        .range(from, from + PAGE - 1);
      if (err) { error = err; break; }
      if (!page || page.length === 0) break;
      allData = allData.concat(page);
      if (page.length < PAGE) break;
      from += PAGE;
    }

    if (error) {
      document.getElementById('ps-tbody').innerHTML =
        `<tr><td colspan="9" class="no-data" style="color:var(--danger)">${esc(error.message)}</td></tr>`;
      return;
    }

    const data = allData;

    rows = (data || []).sort((a, b) =>
      (a.full_name || '').localeCompare(b.full_name || '', 'en', { sensitivity: 'base' })
    );
    await loadStored();
    populateStipendSelect();
    renderRows();
    renderTotals();
    loadWaStatus();
  }

  /* ── Stored payslips for the month (learner pay): status + where the PDF is ── */
  async function loadStored() {
    stored = new Map();
    if (!isAdmin) return;
    for (let from = 0; ; from += 1000) {
      const { data, error } = await supabase.from('learner_payslips')
        .select('id,student_uuid,status,pdf_path')
        .eq('period_year', state.year).eq('period_month', state.month)
        .range(from, from + 999);
      if (error) { console.error('stored payslips:', error.message); return; }
      (data || []).forEach(r => stored.set(r.student_uuid, r));
      if (!data || data.length < 1000) break;
    }
    renderMonthSummary();
  }

  function renderMonthSummary() {
    const el = document.getElementById('ps-month-summary');
    if (!el) return;
    const n = { draft: 0, final: 0, paid: 0, changed: 0 };
    rows.forEach(r => { if (r.payslip_status) n[r.payslip_status] += 1; if (r.register_changed) n.changed += 1; });
    el.innerHTML = `<strong>${monthLabel()}</strong>: ${n.draft} draft · ${n.final} final · ${n.paid} paid` +
      (n.changed ? ` · <span style="color:#b45309">${n.changed} final payslip${n.changed === 1 ? '' : 's'} no longer match the register</span>` : '');
  }

  function monthLabel() {
    return new Date(state.year, state.month - 1, 1).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
  }

  function monthMsg(text, colour = 'var(--text-muted)') {
    const el = document.getElementById('ps-month-msg');
    if (el) { el.style.color = colour; el.textContent = text; }
  }

  /* ── WhatsApp payslip delivery status (admin) ─────────────────────── */
  async function loadWaStatus() {
    if (!isAdmin || !rows.length) return;
    const { data, error } = await supabase.rpc('get_payslip_delivery_status',
      { p_student_uuids: rows.map(r => r.student_uuid) });
    if (error) { console.error('delivery status:', error.message); return; }
    waStatus = new Map((data || []).map(d => [d.student_uuid, d]));
    renderRows();
    renderTotals();
  }

  /** What a WhatsApp send would hold for this learner with the Documents chosen ('' = nothing). */
  function sendWhy(uuid) {
    const w = waStatus.get(uuid);
    if (!w?.has_mobile) return 'No valid cell number on file';
    if (!state.docs.size) return 'Choose at least one document in the Documents dropdown';
    const uif = UIF_KINDS.some(k => state.docs.has(k));
    if (!uif && !(w.sendable > 0)) return 'No finalised payslips yet: finalise a month first (drafts are never sent)';
    return '';
  }

  const WA_LABEL = { requested: 'Opt-in sent', confirmed: 'Sending…', sent: 'Sent', failed: 'Failed' };

  function waCell(uuid) {
    if (!isAdmin) return '';
    const w = waStatus.get(uuid);
    const why = sendWhy(uuid);
    const n = w?.sendable || 0;
    const tip = why || `Send the chosen documents on WhatsApp (${docsLabel().toLowerCase()}${state.docs.has('payslips') ? `; ${n} finalised payslip${n === 1 ? '' : 's'}` : ''}). The learner replies YES first.`;
    const when = w?.status_at ? new Date(w.status_at).toLocaleDateString('en-ZA') : '';
    return `<button class="ps-wa-btn" data-uuid="${esc(uuid)}" ${why ? 'disabled' : ''}
        title="${esc(tip)}">WhatsApp</button>
      ${w?.status ? `<span class="ps-wa-status ${esc(w.status)}">${WA_LABEL[w.status] || esc(w.status)} ${esc(when)}</span>` : ''}
      ${w?.shared_with ? `<span class="ps-wa-status shared" title="Same cell number as: ${esc(w.shared_names || '')}. Only the learners you send to get documents.">Shared with ${w.shared_with}</span>` : ''}`;
  }

  async function sendViaWhatsApp(uuids) {
    if (!state.docs.size) { alert('Choose at least one document in the Documents dropdown.'); return; }
    const reachable = uuids.filter(u => !sendWhy(u));
    const noPhone   = uuids.filter(u => !waStatus.get(u)?.has_mobile).length;
    const nothing   = uuids.length - reachable.length - noPhone;
    if (!reachable.length) {
      alert(noPhone === uuids.length
        ? 'None of the selected learners has a valid cell number on file.'
        : 'None of the selected learners has a finalised payslip yet. Finalise the month first (drafts are never sent), or add the UIF documents in the Documents dropdown.');
      return;
    }
    const shared = reachable.filter(u => waStatus.get(u)?.shared_with > 0).length;
    const chosen = DOCS.filter(([k]) => state.docs.has(k)).map(([, l]) => l).join(', ');
    if (!confirm(
      `Ask ${reachable.length} learner${reachable.length === 1 ? '' : 's'} on WhatsApp if they want their documents?` +
      `\n\nThey get: ${chosen}.` +
      (state.docs.has('payslips') ? ' Payslips: their FINALISED months only, as stored at finalise.' : '') +
      (shared ? `\n\n${shared} of them share a cell number with someone else. Only the learners you send to get documents; the others on that number get nothing.` : '') +
      (noPhone ? `\n\n${noPhone} without a valid cell number will be skipped.` : '') +
      (nothing > 0 ? `\n${nothing} with nothing to send yet will be skipped.` : '') +
      `\n\nEverything goes in one PDF once they reply YES.`)) return;

    const { data: { session } } = await supabase.auth.getSession();
    const requested = [], skipped = [];
    let testMode = false;
    for (let i = 0; i < reachable.length; i += 25) {
      const resp = await fetch('/api/payslip-whatsapp-request', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body:    JSON.stringify({ student_uuids: reachable.slice(i, i + 25), documents: [...state.docs] }),
      });
      const out = await resp.json().catch(() => ({}));
      if (!resp.ok) { alert(`WhatsApp request failed: ${out.error || resp.status}`); break; }
      requested.push(...(out.requested || []));
      skipped.push(...(out.skipped || []));
      testMode = testMode || out.test_mode;
    }

    const lines = [`${requested.length} opt-in request${requested.length === 1 ? '' : 's'} sent.`];
    if (testMode) lines.push('TEST MODE: messages go to the WHATSAPP_OVERRIDE_NUMBER, not the learners.');
    if (skipped.length) {
      lines.push('', `${skipped.length} skipped:`);
      skipped.slice(0, 10).forEach(x => lines.push(`• ${x.name || x.student_uuid}: ${x.reason}`));
      if (skipped.length > 10) lines.push(`• …and ${skipped.length - 10} more`);
    }
    alert(lines.join('\n'));
    await loadWaStatus();
  }

  function matchesStipend(amount, filter) {
    if (!filter) return true;
    const v = Number(amount || 0);
    if (filter === '0')    return v === 0;
    if (filter === '1')    return v >= 1    && v <= 499;
    if (filter === '500')  return v >= 500  && v <= 999;
    if (filter === '1000') return v >= 1000 && v <= 1499;
    if (filter === '1500') return v >= 1500 && v <= 2000;
    return true;
  }

  function populateStipendSelect() { /* options are static */ }

  function matchesPayslip(r, filter) {
    if (!filter)              return true;
    if (filter === 'none')    return !r.payslip_status;
    if (filter === 'changed') return !!r.register_changed;
    return r.payslip_status === filter;
  }

  function matchesPhone(uuid, filter) {
    if (!filter) return true;
    const w = waStatus.get(uuid);
    if (filter === 'none') return !w?.has_mobile;
    if (!w?.has_mobile) return false;
    return filter === 'shared' ? w.shared_with > 0 : !(w.shared_with > 0);
  }

  function visibleRows() {
    return rows.filter(r =>
      matchesPayslip(r, state.payslipStatus) &&
      matchesStipend(r.stipend_this_month, state.stipendAmount) &&
      matchesPhone(r.student_uuid, state.phone)
    );
  }

  function slipCell(s) {
    if (!s.payslip_status) return `<span class="ps-muted">—</span>`;
    const label = { draft: 'Draft', final: 'Final', paid: 'Paid' }[s.payslip_status] || s.payslip_status;
    return `<span class="ps-slip ${esc(s.payslip_status)}">${label}</span>` +
      (s.register_changed ? `<span class="ps-flag" title="The register was edited after this payslip was finalised. The stored payslip is unchanged; an admin can re-open it.">⚠ Register changed</span>` : '');
  }

  function docsButton(s) {
    if (!isAdmin) return `<button class="ps-pdf-btn" disabled title="Learner pay only">⬇ Documents</button>`;
    return `<button class="ps-pdf-btn" data-uuid="${esc(s.student_uuid)}"
      title="Download ${esc(docsLabel().toLowerCase())} for this learner in one PDF (draft payslips are stamped DRAFT)">⬇ Documents</button>`;
  }

  /**
   * The chosen documents for some learners, one PDF: per learner their payslips (every month
   * stored: final/paid as stored, drafts stamped DRAFT), then the UIF documents with the
   * current values (as Preview did on Learner documents; nothing is saved).
   */
  async function downloadDocs(uuids) {
    if (!state.docs.size) { alert('Choose at least one document in the Documents dropdown.'); return; }
    if (uuids.length > MAX_DOWNLOAD) { alert(`Select at most ${MAX_DOWNLOAD} learners at a time to download.`); return; }
    const kinds = UIF_KINDS.filter(k => state.docs.has(k));
    const names = new Map(rows.map(r => [r.student_uuid, r.full_name]));
    const slips = new Map();
    if (state.docs.has('payslips')) {
      for (let i = 0; i < uuids.length; i += 100) {
        const { data, error } = await supabase.from('learner_payslips').select('*')
          .in('student_uuid', uuids.slice(i, i + 100)).order('period_year').order('period_month');
        if (error) throw new Error(error.message);
        for (const lp of data || []) {
          if (!slips.has(lp.student_uuid)) slips.set(lp.student_uuid, []);
          slips.get(lp.student_uuid).push(lp);
        }
      }
    }
    const org = await getOrgSettings();
    const out = await PDFDocument.create();
    const add = async (bytes) => {
      const src = await PDFDocument.load(bytes);
      (await out.copyPages(src, src.getPageIndices())).forEach(p => out.addPage(p));
    };
    const left = [];
    let n = 0;
    for (const uuid of uuids) {
      msg(`Building ${++n} of ${uuids.length}…`);
      const before = out.getPageCount();
      let reason = null;
      for (const b of await payslipPdfs(slips.get(uuid) || [], org)) await add(b);
      if (kinds.length) {
        const { bytes, skipped } = await buildLearnerDocs([uuid], { kinds });
        if (bytes) await add(bytes); else reason = `UIF documents: ${skipped[0]?.reason || 'nothing to print'}`;
      }
      if (out.getPageCount() === before) left.push(`${names.get(uuid) || 'learner'} (${reason || 'no payslips'})`);
      else if (reason) left.push(`${names.get(uuid) || 'learner'} (${reason})`);
    }
    if (!out.getPageCount()) { msg(`Nothing to download.${left.length ? ` ${left.slice(0, 5).join('; ')}.` : ''}`, '#b91c1c'); return; }
    const file = uuids.length === 1 ? `${slug(names.get(uuids[0]))}-documents.pdf` : `documents-${uuids.length}-learners.pdf`;
    downloadPdf(await out.save(), file);
    msg(`Downloaded ${docsLabel().toLowerCase()} for ${uuids.length - left.length} learner${uuids.length - left.length === 1 ? '' : 's'}.` +
        (left.length ? ` Left out: ${left.slice(0, 5).join('; ')}${left.length > 5 ? ` and ${left.length - 5} more` : ''}.` : ''),
        left.length ? '#b45309' : '#15803d');
  }

  /* ── Render table rows ───────────────────────────────────────────────────── */
  function renderRows() {
    const tbody = document.getElementById('ps-tbody');
    const visible = visibleRows();
    if (!visible.length) {
      tbody.innerHTML =
        `<tr><td colspan="9" class="no-data">No active learners with attendance this month yet.</td></tr>`;
      return;
    }

    tbody.innerHTML = visible.map(s => `
      <tr data-uuid="${esc(s.student_uuid)}">
        <td style="padding:8px 6px">
          <input type="checkbox" class="ps-chk" data-uuid="${esc(s.student_uuid)}"
            ${state.selected.has(s.student_uuid) ? 'checked' : ''}>
        </td>
        <td class="ps-drill" data-uuid="${esc(s.student_uuid)}" data-name="${esc(s.full_name)}"
          style="cursor:pointer">${esc(s.national_id || '—')}</td>
        <td class="ps-drill" data-uuid="${esc(s.student_uuid)}" data-name="${esc(s.full_name)}"
          style="cursor:pointer"><strong>${esc(s.full_name)}</strong></td>
        <td style="text-align:right">${s.days_present || 0}</td>
        <td>${esc(s.bank_name || '—')}</td>
        <td>${esc(s.bank_account_number || '—')}</td>
        <td style="text-align:right"><strong>R&nbsp;${Number(s.stipend_this_month || 0).toFixed(2)}</strong></td>
        <td>${slipCell(s)}</td>
        <td style="white-space:nowrap">${docsButton(s)}${waCell(s.student_uuid)}</td>
      </tr>`).join('');

    /* WhatsApp button (admin) */
    tbody.querySelectorAll('.ps-wa-btn').forEach(btn =>
      btn.addEventListener('click', () => sendViaWhatsApp([btn.dataset.uuid]))
    );

    /* Drill on name / ID click */
    tbody.querySelectorAll('.ps-drill').forEach(cell =>
      cell.addEventListener('click', () =>
        openStudentDrill({ studentUuid: cell.dataset.uuid, studentName: cell.dataset.name })
      )
    );

    /* Per-row checkbox */
    tbody.querySelectorAll('.ps-chk').forEach(chk =>
      chk.addEventListener('change', () => {
        if (chk.checked) state.selected.add(chk.dataset.uuid);
        else             state.selected.delete(chk.dataset.uuid);
        updateBulkBar();
        syncSelectAll();
      })
    );

    /* Documents button: the chosen documents for this learner, one PDF */
    tbody.querySelectorAll('.ps-pdf-btn[data-uuid]').forEach(btn =>
      btn.addEventListener('click', async () => {
        const label = btn.textContent;
        btn.disabled = true; btn.textContent = '…';
        try {
          await downloadDocs([btn.dataset.uuid]);
        } catch (err) {
          console.error('Documents error:', err);
          msg(`Could not build the documents: ${err.message}`, '#b91c1c');
        } finally {
          btn.disabled = false; btn.textContent = label;
        }
      })
    );
  }

  /* ── Totals card ─────────────────────────────────────────────────────────── */
  function renderTotals() {
    const visible = visibleRows();
    const n   = visible.length;
    const days = visible.reduce((s, r) => s + (r.days_present || 0), 0);
    const amt  = visible.reduce((s, r) => s + Number(r.stipend_this_month || 0), 0);
    const sep  = `<span style="color:var(--border,#d0d0d0);font-size:1.4rem;line-height:1">|</span>`;
    document.getElementById('ps-totals').innerHTML = `
      <div>
        <span style="font-size:1.35rem;font-weight:700;color:var(--text)">${n}</span>
        <span style="font-size:.8rem;color:var(--text-muted);margin-left:3px">learners</span>
      </div>
      ${sep}
      <div>
        <span style="font-size:1.35rem;font-weight:700;color:var(--text)">${days}</span>
        <span style="font-size:.8rem;color:var(--text-muted);margin-left:3px">days present</span>
      </div>
      ${sep}
      <div>
        <span style="font-size:1.35rem;font-weight:700;color:var(--text)">R&nbsp;${amt.toLocaleString('en-ZA', { minimumFractionDigits:2, maximumFractionDigits:2 })}</span>
        <span style="font-size:.8rem;color:var(--text-muted);margin-left:3px">total stipend</span>
      </div>`;
  }

  /* ── Bulk bar helpers ────────────────────────────────────────────────────── */
  function updateBulkBar() {
    const n = state.selected.size;
    document.getElementById('ps-sel-count').textContent = `${n} selected · ${docsLabel()}`;
    document.getElementById('ps-bulk-bar').classList.toggle('visible', n > 0);
  }

  function syncSelectAll() {
    const chks = [...document.querySelectorAll('.ps-chk')];
    document.getElementById('ps-select-all').checked =
      chks.length > 0 && chks.every(c => c.checked);
  }

  /* ── Finalise (server: freezes the payslips and stores each PDF) ─────────── */
  async function finaliseViaApi(uuids, { finalise = true, quiet = false } = {}) {
    const { data: { session } } = await supabase.auth.getSession();
    let first = true, built = 0, finalised = 0;
    const failed = [];
    while (true) {
      const resp = await fetch('/api/learner-payslips-finalise', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body:    JSON.stringify({ year: state.year, month: state.month, student_uuids: uuids, finalise: finalise && first }),
      });
      const out = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(out.error || `HTTP ${resp.status}`);
      first = false;
      finalised += out.finalised || 0;
      built     += out.built || 0;
      failed.push(...(out.failed || []));
      if (!quiet) monthMsg(`Finalised ${finalised}. PDFs stored: ${built}${out.remaining ? `, ${out.remaining} to go…` : '.'}`);
      if (!out.remaining || !out.built) break;   // done, or a batch made no progress: stop, don't loop
    }
    return { finalised, built, failed };
  }

  /* ── Payslip actions on the selection ────────────────────────────────────── */
  const ACTION_TEXT = {
    finalise:    { verb: 'Finalise', from: 'draft', note: 'Final payslips are frozen: later register changes will not alter them (an admin can re-open). Each PDF is built and stored now.' },
    mark_paid:   { verb: 'Mark as paid', from: 'final', note: 'Only final payslips can be marked paid.' },
    unmark_paid: { verb: 'Undo paid on', from: 'paid', note: 'They go back to final.' },
    reopen:      { verb: 'Re-open', from: 'final', note: 'They go back to draft, are recalculated from the register, and their stored PDF is discarded. Recorded in the audit log.' },
  };

  async function runAction(action, uuids) {
    const t = ACTION_TEXT[action];
    const eligible = uuids.filter(u => rows.find(r => r.student_uuid === u)?.payslip_status === t.from);
    if (!eligible.length) { alert(`None of the selected learners has a ${t.from} payslip for ${monthLabel()}.`); return; }
    const other = uuids.length - eligible.length;
    if (!confirm(`${t.verb} ${eligible.length} payslip${eligible.length === 1 ? '' : 's'} for ${monthLabel()}?` +
                 (other ? `\n\n${other} selected learner${other === 1 ? ' is' : 's are'} not ${t.from} and will be skipped.` : '') +
                 `\n\n${t.note}`)) return;
    try {
      if (action === 'finalise') {
        const r = await finaliseViaApi(eligible);
        monthMsg(`Finalised ${r.finalised}; ${r.built} PDFs stored.${r.failed.length ? ` ${r.failed.length} PDFs failed — open them to retry.` : ''}`,
                 r.failed.length ? '#b91c1c' : '#15803d');
      } else {
        const { data, error } = await supabase.rpc('learner_payslips_set_status',
          { p_year: state.year, p_month: state.month, p_action: action, p_student_uuids: eligible });
        if (error) throw new Error(error.message);
        monthMsg(`${t.verb}: ${data.changed} payslip${data.changed === 1 ? '' : 's'}.`, '#15803d');
      }
    } catch (err) {
      monthMsg(`${t.verb} failed: ${err.message}`, '#b91c1c');
      alert(`${t.verb} failed: ${err.message}`);
    }
    await reload();
  }

  /* ── Wire up controls ────────────────────────────────────────────────────── */

  /* Select-all checkbox */
  document.getElementById('ps-select-all').addEventListener('change', e => {
    document.querySelectorAll('.ps-chk').forEach(c => {
      c.checked = e.target.checked;
      if (e.target.checked) state.selected.add(c.dataset.uuid);
      else                  state.selected.delete(c.dataset.uuid);
    });
    updateBulkBar();
  });

  /* Bulk payslip actions */
  document.querySelectorAll('.ps-bulk-btn').forEach(btn =>
    btn.addEventListener('click', () => runAction(btn.dataset.action, [...state.selected]))
  );

  /* Month bar: recalculate drafts / finalise the drafts shown */
  document.getElementById('ps-recalc')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    btn.disabled = true;
    monthMsg('Recalculating drafts from the register…');
    const { data, error } = await supabase.rpc('learner_payslips_recalculate', { p_year: state.year, p_month: state.month });
    btn.disabled = false;
    if (error) { monthMsg(`Recalculate failed: ${error.message}`, '#b91c1c'); return; }
    await reload();
    monthMsg(`Drafts refreshed: ${data.updated} changed, ${data.inserted} added, ${data.deleted} removed.` +
             (data.register_changed ? ` ${data.register_changed} final/paid payslip${data.register_changed === 1 ? ' no longer matches' : 's no longer match'} the register (left unchanged).` : ''),
             '#15803d');
  });
  document.getElementById('ps-finalise-shown')?.addEventListener('click', () => {
    const drafts = visibleRows().filter(r => r.payslip_status === 'draft').map(r => r.student_uuid);
    if (!drafts.length) { alert('There are no draft payslips in the list as filtered.'); return; }
    runAction('finalise', drafts);
  });
  document.getElementById('ps-wa-bulk')?.addEventListener('click', () =>
    sendViaWhatsApp([...state.selected])
  );
  document.getElementById('ps-dl-bulk')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    btn.disabled = true;
    try { await downloadDocs([...state.selected]); }
    catch (err) { msg(`Could not build the documents: ${err.message}`, '#b91c1c'); }
    finally { btn.disabled = false; }
  });

  /* Documents dropdown: re-label, and the row buttons follow it */
  document.querySelectorAll('.ps-doc').forEach(c => c.addEventListener('change', () => {
    c.checked ? state.docs.add(c.value) : state.docs.delete(c.value);
    showDocsLabel();
    renderRows();
  }));
  if (!_docsCloserWired) {   // once: the page re-renders, the document listener stays
    _docsCloserWired = true;
    document.addEventListener('click', (e) => {
      const d = document.getElementById('ps-docs');
      if (d?.open && !d.contains(e.target)) d.open = false;
    });
  }

  /* Cell number filter (client-side, from the delivery status) */
  document.getElementById('ps-phone').addEventListener('change', e => {
    state.phone = e.target.value;
    renderRows();
    renderTotals();
  });

  /* Exports never include test learners (e.g. "Test Payslip"), so a test payslip can't reach Sage or the bank. */
  const realRows = () => rows.filter(r => r.learner_status !== 'test');

  /* CSV export */
  document.getElementById('ps-csv-btn').addEventListener('click', () => {
    if (!realRows().length) { alert('No data to export.'); return; }
    exportToCSV(realRows(), state.year, state.month);
  });

  /* Standard Bank bulk payment export */
  document.getElementById('ps-stdbank-btn').addEventListener('click', () => {
    if (!realRows().length) { alert('No data to export.'); return; }
    exportStandardBank(realRows(), state.year, state.month);
  });

  /* Month filter */
  document.getElementById('ps-month').addEventListener('change', e => {
    const [y, m] = e.target.value.split('-').map(Number);
    if (y && m) { state.year = y; state.month = m; reload(); }
  });

  /* Populate class dropdown based on current municipality */
  function populateClassSelect() {
    const sel = document.getElementById('ps-class');
    const filtered = state.municipality
      ? classes.filter(c => c.municipality_id === state.municipality)
      : classes;
    sel.innerHTML = '<option value="">All classes</option>' +
      filtered.map(c => `<option value="${esc(c.class_id)}">${esc(c.class_name)}</option>`).join('');
  }

  /* Municipality filter */
  document.getElementById('ps-muni').addEventListener('change', e => {
    state.municipality = e.target.value || null;
    state.classId = null;
    populateClassSelect();
    document.getElementById('ps-class').value = '';
    reload();
  });

  /* Class filter */
  document.getElementById('ps-class').addEventListener('change', e => {
    state.classId = e.target.value || null;
    reload();
  });

  /* Learner status filter (server-side) */
  document.getElementById('ps-learner-status').addEventListener('change', e => {
    state.learnerStatus = e.target.value;
    reload();
  });

  /* Payslip status filter (client-side) */
  document.getElementById('ps-payslip-status').addEventListener('change', e => {
    state.payslipStatus = e.target.value;
    renderRows();
    renderTotals();
  });

  /* Stipend amount filter (client-side) */
  document.getElementById('ps-stipend').addEventListener('change', e => {
    state.stipendAmount = e.target.value;
    renderRows();
    renderTotals();
  });

  populateClassSelect();
  document.getElementById('ps-class').value = state.classId || '';
  showDocsLabel();
  await reload();
}

/* ── CSV export (Sage Payroll format, 22 columns) ──────────────────────────── */
function exportToCSV(rows, year, month) {
  const headers = [
    'Employee Number', 'Initial', 'Surname', 'First Name', 'Gender', 'Marital Status',
    'ID Number', 'Start Date', 'Termination Date', 'Tax Year Start',
    'UIF Status', 'Tax Status', 'Directive %', 'Payment Method',
    'Bank Name', 'Bank Account Type', 'Branch Code', 'Bank Account Number',
    'Days Per Period', 'Amount', 'Address', 'Date of Birth',
  ];

  const fmtDate  = d => d ? String(d).split('T')[0] : '';
  const taxStart = `01/03/${year}`;

  const dataRows = rows.map(r => [
    r.employee_number     ?? '',
    r.first_name?.[0]     ?? '',
    r.surname             ?? '',
    r.first_name          ?? '',
    r.gender              ?? '',
    r.marital_status      ?? '',
    r.national_id         ?? '',
    fmtDate(r.start_date),
    fmtDate(r.termination_date),
    taxStart,
    r.uif_contributor ? 'Y' : 'N',
    r.tax_status          ?? '',
    r.directive_pct       ?? '',
    r.payment_method      ?? '',
    r.bank_name           ?? '',
    r.bank_account_type   ?? '',
    r.branch_code         ?? '',
    r.bank_account_number ?? '',
    r.days_present        ?? 0,
    Number(r.stipend_this_month || 0).toFixed(2),
    r.residential_address ?? '',
    fmtDate(r.date_of_birth),
  ].map(csvCell));

  const csv  = [headers.map(csvCell), ...dataRows].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `stipend-${year}-${String(month).padStart(2,'0')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function csvCell(v) {
  const s = String(v ?? '');
  return (s.includes(',') || s.includes('"') || s.includes('\n'))
    ? `"${s.replace(/"/g, '""')}"`
    : s;
}

/* ── Standard Bank bulk payment CSV ────────────────────────────────────────── */
function exportStandardBank(rows, year, month) {
  const FROM_BRANCH = '050818';

  // Prompt for account number so it always matches the portal exactly
  const savedAccount = localStorage.getItem('sb_from_account') || '280451601';
  const inputAccount = window.prompt(
    'Confirm your Standard Bank "From Account" number\n(check Accounts and Products on your portal):',
    savedAccount
  );
  if (!inputAccount) return; // user cancelled
  const FROM_ACCOUNT = inputAccount.trim().replace(/[-\s]/g, '');
  localStorage.setItem('sb_from_account', FROM_ACCOUNT);

  const payable = rows.filter(r => r.bank_account_number && r.branch_code);
  const skipped  = rows.length - payable.length;

  if (!payable.length) {
    alert('No learners with bank account details to export.');
    return;
  }

  const total = payable.reduce((s, r) => s + Number(r.stipend_this_month || 0), 0);

  const today = new Date();
  const dd    = String(today.getDate()).padStart(2, '0');
  const mm    = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy  = today.getFullYear();
  const execDate  = `${dd}/${mm}/${yyyy}`;
  const batchRef  = `STIPEND-${year}-${String(month).padStart(2, '0')}`;

  // Issue 2 fix: strip commas, apostrophes, collapse spaces
  const sanitise = s => String(s ?? '')
    .replace(/[,']/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  // Issue 1 fix: strip dashes/spaces AND convert scientific notation to plain integer
  const normaliseNum = s => {
    const cleaned = String(s ?? '').replace(/[-\s]/g, '');
    if (/^[\d.]+[eE][+\-]?\d+$/i.test(cleaned)) {
      return String(Math.round(Number(cleaned)));
    }
    return cleaned;
  };

  // Exclude learners with R0.00 — Standard Bank rejects zero-amount rows
  const billable = payable.filter(r => Number(r.stipend_this_month || 0) > 0);
  const zeroed   = payable.length - billable.length;

  if (!billable.length) {
    alert('No learners with a payable amount this month.');
    return;
  }

  const lines = [
    // <<BB_HEADER>> must be Line 1
    `<<BB_HEADER>>,BULK,${FROM_ACCOUNT},0.00,${batchRef},${execDate}`,
    // BULK_DOMESTIC is Line 2
    `BULK_DOMESTIC,${FROM_BRANCH},${FROM_ACCOUNT},${total.toFixed(2)},${batchRef},${execDate}`,
    // One payment row per learner
    ...billable.map(r => [
      sanitise(r.full_name),
      normaliseNum(r.branch_code),
      normaliseNum(r.bank_account_number),
      Number(r.stipend_this_month || 0).toFixed(2),
      sanitise(r.full_name),
      '',
    ].join(',')),
  ];

  const csv  = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `standard-bank-bulk-${year}-${String(month).padStart(2, '0')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);

  const notices = [];
  if (skipped)  notices.push(`${skipped} excluded — no bank account details on file`);
  if (zeroed)   notices.push(`${zeroed} excluded — R0.00 amount (zero attendance or data issue)`);
  if (notices.length) alert(`Note:\n• ${notices.join('\n• ')}`);

}
